// src/pages/admin/UserManagementPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db, auth } from '../../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Select, MenuItem, FormControl, InputLabel, CircularProgress, Button, Box, TextField, Alert
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

function UserManagementPage() {
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterName, setFilterName] = useState('');
    const [filterPerfil, setFilterPerfil] = useState('');
    const [changes, setChanges] = useState({});
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        const snapshot = await getDocs(collection(db, "usuarios"));
        const usersData = snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
        usersData.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
        setAllUsers(usersData);
        setLoading(false);
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const handleRoleChange = (userId, newRole) => {
        setChanges(prev => ({ ...prev, [userId]: newRole }));
    };

    const handleSaveChanges = async () => {
        setSaving(true);
        try {
            const updatedByEmail = auth.currentUser?.email || 'Administrador';
            const updates = Object.keys(changes).map(userId => {
                const userRef = doc(db, "usuarios", userId);
                return updateDoc(userRef, {
                    tipoUsuario: changes[userId],
                    _updatedBy: { email: updatedByEmail, at: new Date() }
                });
            });
            await Promise.all(updates);
            setMessage('Alterações aplicadas com sucesso!');
            setChanges({});
            await fetchUsers();
        } catch (error) {
            console.error(error);
            setMessage('Erro ao salvar.');
        }
        setSaving(false);
    };

    const filteredUsers = useMemo(() => {
        const busca = filterName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        return allUsers.filter(u => {
            const nome      = (u.nome  || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const email     = (u.email || '').toLowerCase();
            const buscaOk   = !filterName || nome.includes(busca) || email.includes(busca);
            const perfilAtual = changes[u.id] || u.tipoUsuario;
            const perfilOk  = !filterPerfil || perfilAtual === filterPerfil;
            return buscaOk && perfilOk;
        });
    }, [allUsers, filterName, filterPerfil, changes]);

    return (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    label="Buscar por nome ou e-mail..."
                    size="small"
                    sx={{ minWidth: 260 }}
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Filtrar por Perfil</InputLabel>
                    <Select
                        value={filterPerfil}
                        label="Filtrar por Perfil"
                        onChange={(e) => setFilterPerfil(e.target.value)}
                    >
                        <MenuItem value=""><em>Todos os perfis</em></MenuItem>
                        <MenuItem value="administrador">Administrador</MenuItem>
                        <MenuItem value="gerente">Gerente</MenuItem>
                        <MenuItem value="autonomo">Autônomo</MenuItem>
                        <MenuItem value="imobiliaria">Imobiliária</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="outlined" size="small" onClick={() => { setFilterName(''); setFilterPerfil(''); }}>
                    Limpar
                </Button>
                <Box sx={{ ml: 'auto' }}>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveChanges} disabled={Object.keys(changes).length === 0 || saving}>
                        {saving ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                </Box>
            </Box>

            {message && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>E-mail</TableCell>
                            <TableCell>Perfil</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={3} align="center"><CircularProgress /></TableCell></TableRow>
                        ) : filteredUsers.length === 0 ? (
                            <TableRow><TableCell colSpan={3} align="center">Nenhum usuário encontrado.</TableCell></TableRow>
                        ) : (
                            filteredUsers.map(u => (
                                <TableRow key={u.id}>
                                    <TableCell>{u.nome}</TableCell>
                                    <TableCell>{u.email}</TableCell>
                                    <TableCell>
                                        <FormControl size="small" sx={{ minWidth: 150 }}>
                                            <Select value={changes[u.id] || u.tipoUsuario} onChange={(e) => handleRoleChange(u.id, e.target.value)}>
                                                <MenuItem value="administrador">Administrador</MenuItem>
                                                <MenuItem value="gerente">Gerente</MenuItem>
                                                <MenuItem value="autonomo">Autônomo</MenuItem>
                                                <MenuItem value="imobiliaria">Imobiliária</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

export default UserManagementPage;
